import React, { Component, Children } from "react"
import classNames from "classnames"
import { Link } from "react-router"
import { browserHistory } from "react-router"
import { throttle, debounce } from "lodash/function"
import auth from "../../auth"
import { dispatch } from "../../Dispatcher"
import { setToken, setLocale } from "../../actions"
import { trackEvent } from "../../analyticsAndSocial"
import State from "../../stores/State"
import { outerHeight } from "../../scripts/utils"
import Icon from "../elements/Icon"
import { getLastSearchUrl } from "../pages/Search"
import { FormattedMessage, injectIntl, FormattedHTMLMessage } from "react-intl"
import sharedMessages from "../../messages/index"
import headerMessages from "../../messages/header"
import settingsMessages from "../../messages/settings"
import notificationMessages from "../../messages/notification"
import { setPref } from "../../scripts/prefs"
import BadgeLink from "../elements/BadgeLink"
import Badge from "../elements/Badge"
import { isAdmin } from "../../scripts/user"
import Notification from "./Notification"
import OnboardingModal from "./OnboardingModal"
import { getAccountSettings } from "../../scripts/accountSettings"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"
import Image from "../elements/Image"

class Header extends Component {
  constructor(props) {
    super(props)

    this.state = {
      searchText: "",
      searchFocused: State.get("searchFocused"),
      loggedIn: State.get("loggedIn"),
      locale: State.get("locale"),
      userIsAdmin: isAdmin(),
      showModal: false,
      currentStep: 1
    }

    this._scrollBefore = 0
    this._el = null

    //Throttle calls to this as we will listen on scroll
    this._stickHeader = throttle(this._stickHeader, 300)
    //Adding a debounce here to ignore blur events between focus, ie moving from input to button
    this._handleSearchFocus = debounce(this._handleSearchFocus, 300)
  }

  componentWillReceiveProps() {
    //Reset the header when we change pages, timeout to give time to set this._el ref
    setTimeout(() => this._stickHeader(), 300)
  }

  _setFromGlobalState = () => {
    const loggedIn = State.get("loggedIn")
    const searchFocused = State.get("searchFocused")
    const locale = State.get("locale")
    const userIsAdmin = isAdmin()

    if (
      !(
        loggedIn === this.state.loggedIn &&
        searchFocused === this.state.searchFocused &&
        locale === this.state.locale &&
        userIsAdmin === this.state.userIsAdmin
      )
    ) {
      this.setState({
        searchFocused,
        loggedIn,
        locale,
        userIsAdmin
      })
    }
  }

  componentDidMount() {
    this._stateListener = State.addListener(() => {
      this._setFromGlobalState()
    })

    //Stick header on scroll
    window.addEventListener("scroll", this._stickHeader)

    this._stickHeader()
  }

  componentWillUnmount() {
    if (this._stateListener) {
      this._stateListener.remove()
    }

    window.removeEventListener("scroll", this._stickHeader)
  }

  _search = () => {
    const searchText = this.state.searchText

    if (this.props.pathname === "/collections") {
      dispatch({ type: "search", q: this.state.searchText })

      browserHistory.push('/collections' + (searchText ? `?q=${this.state.searchText}` : ""))

      return
    }

    if (!this._maybeNavigateToSearchPage()) {
      if (this.state.searchText) {
        dispatch({ type: "search", q: searchText })
      } else {
        dispatch({ type: "search", q: "" })
      }
    }

    //Clear this as it will show on the search page header now
    this.setState(
      {
        searchText: ""
      },
      () =>
        setTimeout(() => {
          // TODO: replace timeout to callback in the next major update
          if (typeof window === "undefined") return
          window.scrollTo(0, 0)
        }, 1000)
    )
  }

  _maybeNavigateToSearchPage = () => {
    if (this.props.pathname !== "/search") {
      browserHistory.push(
        "/search" + (this.state.searchText ? `?q=${this.state.searchText}` : "")
      )
      dispatch({ type: "search", q: this.state.searchText })
      return true
    }
    return false
  }

  //Sticky header logic
  _stickHeader = () => {
    if (!this._el) {
      return
    }

    const headHeight = outerHeight(this._el)
    const wScrollCurrent = window.pageYOffset
    const wScrollDiff = this._scrollBefore - wScrollCurrent
    const chromeTriggerPoint = this._getChromeTriggerPoint()
    const headerHidden = State.get("headerHidden")
    const navExpanded = State.get("navExpanded")

    if (chromeTriggerPoint !== false) {
      if (chromeTriggerPoint && wScrollCurrent > chromeTriggerPoint) {
        dispatch({ type: "view-has-chrome", value: true })
      } else {
        dispatch({ type: "view-has-chrome", value: false })
      }
    } else {
      dispatch({ type: "view-has-chrome", value: true })
    }

    if (
      wScrollCurrent <= headHeight ||
      // ^ scrolled to the very top; element sticks to the top
      (wScrollDiff > 0 && headerHidden)
      // ^ scrolled up; element slides in
    ) {
      dispatch({ type: "header-hidden", value: false })
    } else if (wScrollDiff < 0 && !navExpanded) {
      // scrolled down; hide header BUT only if the nav isn't open
      dispatch({ type: "header-hidden", value: true })
    }

    this._scrollBefore = wScrollCurrent
  }

  // Used by sticky header logic above
  _getChromeTriggerPoint = () => {
    const chromeTrigger = document.querySelectorAll(".js-trigger-chrome")
    return chromeTrigger.length > 0
      ? chromeTrigger[0].getBoundingClientRect.top + document.body.scrollTop
      : false
  }

  //
  // HANDLERS
  //

  _handleNavTrigger = () => {
    dispatch({ type: "toggle-nav" })
  }

  _handleSearch = e => {
    e.preventDefault()

    // hmm, the only case this doesn't catch is if on small screen you enter a term, then focus elsewhere, then click search, it'll fire the search on a term you cant see (cos smallscreen field is hidden when not focussed)... ohwell..
    if (!this.state.searchFocused && !this.state.searchText) {
      dispatch({ type: "search-focused", focused: true })
    } else if (this.state.searchFocused && !this.state.searchText) {
      dispatch({ type: "search-focused", focused: false })
    } else {
      this._search()
    }
  }

  _handleAddTerm = e => {
    e.preventDefault()
    dispatch({ type: "add-query-term", term: this.state.searchText })

    this.setState({
      searchText: ""
    })
  }

  _handleSearchFocus = focused => {
    dispatch({ type: "search-focused", focused: focused })
  }

  _handleSearchChange = e => {
    this.setState({ searchText: e.target.value })
  }

  _handleExplore = e => {
    e.preventDefault()
    this.setState({ searchText: "" }, () => {
      browserHistory.push("/search")
    })
  }

  _setLocale = locale => {
    dispatch(setLocale(locale))
    setPref({ locale })
  }

  handleLogout = e => {
    e.preventDefault()
    auth.logout()
    dispatch(setToken(auth.getAccessToken()))
    trackEvent("user", "logout")
    browserHistory.push("/")
  }

  _headNavLink(props) {
    return (
      <Link
        {...props}
        className={classNames("site-header__nav-link ", props.className)}
        role="menuitem"
      >
        {Children.only(props.children)}
      </Link>
    )
  }

  _notificationDetails = resources => {
    const orderedResources = resources.sort(
      (a, b) => (a.created_at < b.created_at ? 1 : -1)
    )
    const notification = orderedResources.find(function(resource) {
      return resource.type == "notification"
    })
    return notification ? notification : false
  }

  _browserSupportMessage = () => {
    return {
      id: notificationMessages.browserSupport.id,
      description: notificationMessages.browserSupport.defaultMessage
    }
  }

  _browserNotSupported = () => {
    return navigator.userAgent.indexOf("Trident") >= 0
  }

  _onboardingSteps = resources => {
    const steps = resources.filter(
      resource => resource.type == "onboarding_step"
    )
    steps.sort(
      (a, b) =>
        b.position === null
          ? -1
          : a.position === null || b.position < a.position
            ? 1
            : -1
    )
    return steps.length > 0 ? steps : false
  }

  openTourModal = () => {
    this.setState({ showModal: true })
  }

  handleCloseModal = () => {
    this.setState({ showModal: false, currentStep: 1 })
  }

  updateCurrentStep(currentStep) {
    this.setState({ currentStep })
  }

  render() {
    const HeadNavLink = this._headNavLink
    const { resources, intl, pathname } = this.props
    const { showModal, searchText, loggedIn, locale, userIsAdmin } = this.state
    const onboardingSteps = this._onboardingSteps(resources)
    // const { searchText, loggedIn, locale } = this.state
    // const userIsAdmin = true
    const showAddTerm = pathname === "/search" && searchText.length > 2
    const backToSearchUrl = getLastSearchUrl()
    const {
      settings: {
        style: { header: styleType },
        footer_pages: footerPages,
        twitter_handle: twitterHandle,
        instagram_handle: instagramHandle,
        facebook_handle: facebookHandle,
        contact_email: contactEmail,
        homepage_enabled: homepageEnabled,
        contributions_enabled: contributionsEnabled
      },
      logo: siteLogo,
      name: siteName
    } = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )

    return (
      <header
        className={`site-header ${styleType}`}
        role="banner"
        ref={el => (this._el = el)}
        style={getStyles("site-header")}
      >
        <div className="site-header-inner">
          {getStyleTag([
            "site-header",
            "site-header__col",
            "site-header__col-inner",
            "site-header__nav-link--main",
            "site-header__nav-link",
            "site-header__nav-link--profile__icon",
            "site-header-search__input",
            "site-header-search__button",
            "site-header__nav-trigger",
            "auth-site-header",
            "input-group-button__button"
          ])}
          {onboardingSteps ? (
            <OnboardingModal
              showModal={showModal}
              resources={onboardingSteps}
              closeModal={this.handleCloseModal}
              currentStep={this.state.currentStep}
              updateCurrentStep={this.updateCurrentStep.bind(this)}
            />
          ) : null}
          <div className="row" style={getStyles("site-header__container")}>
            <div className="small-12 columns site-header__inner">
              <div
                className="logo site-header__col site-header__col--logo"
                style={getStyles([
                  "site-header__col--logo",
                  "site-header__col"
                ])}
              >
                <Link
                  to={homepageEnabled ? "/" : "/search"}
                  className="site-header__col-inner"
                  style={getStyles("site-header__col-inner--logo")}
                >
                  <Image
                    className="logo__image"
                    src={siteLogo ? siteLogo.url : ""}
                    alt={siteName}
                  />
                </Link>
              </div>
              <div
                className="c-search js-site-search site-header__col site-header__col--search"
                role="search"
                style={getStyles("site-header__col")}
              >
                <form
                  className={classNames(
                    "c-search__form js-site-search-form input-group",
                    { "show-add-term": showAddTerm }
                  )}
                  onBlur={() => this._handleSearchFocus(false)}
                  action=""
                  style={getStyles("input-group")}
                >
                  <label className="show-for-sr" htmlFor="site-search">
                    <FormattedMessage {...headerMessages.search} />
                  </label>
                  {pathname.match(/^\/item/) && (
                    <div className="c-search__button-holder input-group-button">
                      <button
                        type="button"
                        className="c-search__button c-search__button--back  button"
                        title={intl.formatMessage(headerMessages.backToSearch)}
                        onClick={e => {
                          e.preventDefault()
                          browserHistory.push(backToSearchUrl)
                        }}
                        style={getStyles("input-group-button__button")}
                      >
                        <Icon icon="arrow-left" />
                      </button>
                    </div>
                  )}
                  <input
                    className="c-search__input js-site-search-input input-group-field"
                    type="search"
                    placeholder={intl.formatMessage(headerMessages.searchMore)}
                    value={this.state.searchText}
                    name="site-search"
                    id="site-search"
                    aria-label={intl.formatMessage(headerMessages.searchMore)}
                    onFocus={() => this._handleSearchFocus(true)}
                    onKeyDown={e => {
                      if (e.keyCode === 13) this._search()
                    }}
                    style={getStyles("site-header-search__input")}
                    onChange={this._handleSearchChange}
                  />
                  <input type="text" style={{ display: "none" }} aria-hidden />
                  {showAddTerm && (
                    <div className="c-search__button-holder input-group-button">
                      <button
                        type="button"
                        className="c-search__button c-search__button--add button"
                        title={intl.formatMessage(headerMessages.addTermPhrase)}
                        aria-label={intl.formatMessage(
                          headerMessages.addTermPhrase
                        )}
                        onClick={this._handleAddTerm}
                        style={getStyles("input-group-button__button")}
                      >
                        <Icon icon="plus" />
                      </button>
                    </div>
                  )}
                  <div className="c-search__button-holder input-group-button">
                    <button
                      type="button"
                      className="c-search__button c-search__button--new js-site-search-new button"
                      title={intl.formatMessage(headerMessages.newTermPhrase)}
                      aria-label={intl.formatMessage(
                        headerMessages.newTermPhrase
                      )}
                      onClick={this._handleSearch}
                      style={getStyles("input-group-button__button")}
                    >
                      <Icon
                        icon="search"
                        style={getStyles("input-group-button__search-icon")}
                      />
                    </button>
                  </div>
                </form>
              </div>
              <nav
                className="site-header__nav-holder site-header__col site-header__col--nav"
                role="navigation"
                aria-label="Site Navigation"
                style={getStyles(["site-header__col--nav", "site-header__col"])}
              >
                <div
                  className="site-header__col-inner"
                  style={getStyles("site-header__col-inner")}
                >
                  <button
                    onClick={this._handleNavTrigger}
                    className="site-header__nav-trigger js-site-nav-trigger c-button-close"
                    style={getStyles("site-header__nav-trigger")}
                  >
                    <FormattedMessage {...headerMessages.toggleNavigation} />
                  </button>
                  <div className="site-header__nav-mob-scroller">
                    <div className="site-header__nav" role="menu">
                      <div
                        className="site-header__nav-col"
                        style={getStyles("site-header__nav-col")}
                      >
                        <div className="site-header__nav-dropdown-wrapper">
                          <a
                            onClick={this._handleExplore}
                            href="/search"
                            className="site-header__nav-link site-header__nav-link--main"
                            style={getStyles("site-header__nav-link")}
                            role="menuitem"
                          >
                            <FormattedHTMLMessage
                              {...headerMessages.exploreMultiWithLineBreak}
                            />
                          </a>

                          <HeadNavLink
                            className="site-header__nav-link--main site-header__small-form-only"
                            to="/featured-collections"
                          >
                            <FormattedHTMLMessage
                              {...headerMessages.collectionsMultiWithLineBreak}
                            />
                          </HeadNavLink>

                          {contributionsEnabled && (
                            <HeadNavLink
                              to="/contribute"
                              className="site-header__nav-link--main site-header__small-form-only"
                            >
                              <FormattedHTMLMessage
                                {...headerMessages.contributeMultiWithLineBreak}
                              />
                            </HeadNavLink>
                          )}

                          <HeadNavLink
                            className={classNames(
                              "site-header__nav-link--main site-header__small-form-only",
                              this.state.loggedIn && "site-header-with-items"
                            )}
                            to={this.state.loggedIn ? "/user/sets" : "/login"}
                          >
                            <FormattedHTMLMessage
                              {...headerMessages.myScrapbookMultiWithLineBreak}
                            />
                          </HeadNavLink>

                          {this.state.loggedIn && contributionsEnabled && (
                            <HeadNavLink
                              className="site-header__small-form-only site-header__nav-link--secondary"
                              to="/sets"
                            >
                              <FormattedHTMLMessage {...headerMessages.sets} />
                            </HeadNavLink>
                          )}
                          {this.state.loggedIn && contributionsEnabled && (
                            <HeadNavLink
                              className="site-header__small-form-only  site-header__nav-link--secondary"
                              to="/user/contributions"
                            >
                              <FormattedHTMLMessage
                                {...headerMessages.contributions}
                              />
                            </HeadNavLink>
                          )}

                          <ul
                            className="site-header__nav-dropdown"
                            style={getStyles("site-header__nav-dropdown")}
                          >
                            <li>
                              <HeadNavLink to="/search">
                                <FormattedMessage {...headerMessages.browse} />
                              </HeadNavLink>
                            </li>
                            <li>
                              <HeadNavLink to="/featured-collections">
                                <FormattedMessage
                                  {...headerMessages.collections}
                                />
                              </HeadNavLink>
                            </li>
                            {!this.state.loggedIn && (
                              <li>
                                <HeadNavLink
                                  to="/login"
                                  className="site-header__nav-link--main"
                                >
                                  <strong>
                                    <FormattedMessage
                                      {...headerMessages.myScrapbook}
                                    />
                                  </strong>
                                </HeadNavLink>
                              </li>
                            )}
                            {this.state.loggedIn && (
                              <li>
                                <HeadNavLink to="/user/sets">
                                  <FormattedMessage {...headerMessages.sets} />
                                </HeadNavLink>
                              </li>
                            )}
                            {this.state.loggedIn && contributionsEnabled && (
                              <li>
                                <HeadNavLink to="/user/contributions">
                                  <FormattedMessage
                                    {...headerMessages.contributions}
                                  />
                                </HeadNavLink>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                      <div
                        className={classNames("site-header__nav-col", !contributionsEnabled && "site-header__nav-col-hidden")}
                        style={getStyles("site-header__nav-col")}
                      >
                        {contributionsEnabled && (
                          <HeadNavLink
                            to="/contribute"
                            className="site-header__nav-link--main site-header__large-form-only"
                            style={getStyles("site-header__nav-link")}
                          >
                            <FormattedHTMLMessage
                              {...headerMessages.contributeMultiWithLineBreak}
                            />
                          </HeadNavLink>
                        )}

                        {loggedIn && (
                          <HeadNavLink
                            className="site-header__small-form-only site-header__nav-link--secondary"
                            to="/user/profile"
                          >
                            <FormattedMessage {...headerMessages.profile} />
                          </HeadNavLink>
                        )}
                        {!loggedIn && (
                          <HeadNavLink
                            to="/login"
                            className="site-header__small-form-only site-header__nav-link--secondary"
                            role="menuitem"
                          >
                            <FormattedMessage
                              {...headerMessages.loginRegister}
                            />
                          </HeadNavLink>
                        )}
                        {loggedIn && (
                          <a
                            href="#"
                            onClick={this.handleLogout}
                            className="site-header__small-form-only site-header__nav-link site-header__nav-link--secondary"
                            style={getStyles("site-header__nav-link")}
                            role="menuitem"
                          >
                            <FormattedMessage {...headerMessages.logout} />
                          </a>
                        )}
                      </div>
                      <div
                        className="site-header__nav-col"
                        style={getStyles("site-header__nav-col--login-block")}
                      >
                        <div className="site-header__nav-dropdown-wrapper">
                          <Link
                            to={loggedIn ? "/user/profile" : "/login"}
                            role="menuitem"
                            className="man site-header__nav-link--profile"
                            style={getStyles(
                              "site-header__nav-link--profile__icon"
                            )}
                          >
                            <Icon icon="user-circle" />
                          </Link>
                          <ul
                            className="site-header__nav-dropdown site-header__nav-dropdown--right"
                            style={getStyles("site-header__nav-dropdown")}
                          >
                            {loggedIn && (
                              <li>
                                <HeadNavLink
                                  to="/user/profile"
                                  className="site-header__nav-link site-header__nav-link--secondary"
                                >
                                  <FormattedMessage
                                    {...headerMessages.profile}
                                  />
                                </HeadNavLink>
                              </li>
                            )}
                            {loggedIn && (
                              <li>
                                <a
                                  href="#"
                                  onClick={this.handleLogout}
                                  className="site-header__nav-link site-header__nav-link--secondary"
                                  role="menuitem"
                                >
                                  <FormattedMessage
                                    {...headerMessages.logout}
                                  />
                                </a>
                              </li>
                            )}
                            {!loggedIn && (
                              <li>
                                <Link
                                  to="/login"
                                  className="site-header__nav-link site-header__nav-link--secondary"
                                  role="menuitem"
                                >
                                  <FormattedMessage {...headerMessages.login} />
                                </Link>
                              </li>
                            )}
                            {!loggedIn && (
                              <li>
                                <Link
                                  to="/register"
                                  className="site-header__nav-link site-header__nav-link--secondary"
                                  role="menuitem"
                                >
                                  <FormattedMessage
                                    {...headerMessages.register}
                                  />
                                </Link>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                      {onboardingSteps ? (
                        <div
                          className="site-header__nav-col"
                          style={getStyles("site-header__nav-col")}
                        >
                          <a
                            role="menuitem"
                            className="site-header__nav-link--tour"
                            href="#"
                            onClick={this.openTourModal}
                          >
                            <Image
                              src="/images/help-icon.svg"
                              alt="Open Onboarding Tour"
                            />
                          </a>
                        </div>
                      ) : null}
                      {styleType === "default" ? (
                        <div
                          className="site-header__nav-col site-header__nav-col--language"
                          style={getStyles("site-header__nav-col")}
                        >
                          {locale === "mi" && (
                            <span>
                              <Badge extraClasses="site-header__small-form-only-inline-block">
                                Māori
                              </Badge>
                              <Badge
                                badgeStyle={"white"}
                                extraClasses="site-header__large-form-only-inline-block"
                              >
                                Māori
                              </Badge>
                              <br />
                              <BadgeLink
                                badgeStyle={"transparent"}
                                onClick={() => this._setLocale("en")}
                              >
                                English
                              </BadgeLink>
                            </span>
                          )}
                          {locale === "en" && (
                            <span>
                              <BadgeLink
                                badgeStyle={"transparent"}
                                onClick={() => this._setLocale("mi")}
                              >
                                Māori
                              </BadgeLink>
                              <br />
                              <Badge extraClasses="site-header__small-form-only-inline-block">
                                English
                              </Badge>
                              <Badge
                                badgeStyle={"white"}
                                extraClasses="site-header__large-form-only-inline-block"
                              >
                                English
                              </Badge>
                            </span>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <ul className="site-header__nav site-header__nav--secondary">
                      {footerPages
                        ? footerPages.map((item, key) => {
                            if (!item.source_id) {
                              return null
                            }

                            const props = {
                              id: item.source_id,
                              defaultMessage: item.title
                            }

                            return (
                              <li key={key}>
                                <HeadNavLink to={`/${item.source_id}`}>
                                  <FormattedMessage {...props} />
                                </HeadNavLink>
                              </li>
                            )
                          })
                        : null}
                    </ul>
                    <ul className="site-header__nav site-header__nav--social">
                      {twitterHandle && (
                        <li style={getStyles("site-header__nav--social")}>
                          <a
                            className="site-header__nav-link"
                            role="menuitem"
                            href={`https://twitter.com/${twitterHandle}`}
                            target="_blank"
                          >
                            <Icon
                              icon="twitter-circle"
                              title={intl.formatMessage(
                                sharedMessages.findTwitter
                              )}
                            />
                          </a>
                        </li>
                      )}
                      {instagramHandle && (
                        <li style={getStyles("site-header__nav--social")}>
                          <a
                            className="site-header__nav-link"
                            role="menuitem"
                            href={`https://www.instagram.com/${instagramHandle}`}
                            target="_blank"
                          >
                            <Icon
                              icon="instagram-circle"
                              title={intl.formatMessage(
                                sharedMessages.findInstagram
                              )}
                            />
                          </a>
                        </li>
                      )}
                      {facebookHandle && (
                        <li style={getStyles("site-header__nav--social")}>
                          <a
                            className="site-header__nav-link"
                            role="menuitem"
                            href={`https://www.facebook.com/${facebookHandle}`}
                            target="_blank"
                          >
                            <Icon
                              icon="facebook-circle"
                              title={intl.formatMessage(
                                sharedMessages.findFacebook
                              )}
                            />
                          </a>
                        </li>
                      )}
                      {contactEmail && (
                        <li style={getStyles("site-header__nav--social")}>
                          <a
                            className="site-header__nav-link"
                            role="menuitem"
                            href={`mailto:${contactEmail}`}
                          >
                            <Icon
                              icon="email-circle"
                              title={intl.formatMessage(
                                sharedMessages.findEmail
                              )}
                            />
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </nav>
            </div>
          </div>
          {this._browserNotSupported() && (
            <Notification
              details={this._browserSupportMessage()}
              extraClasses={"browser-support"}
              browserSupport={true}
              bodyClass={"browser-support-visible"}
              description={intl.formatMessage(
                notificationMessages.browserSupport
              )}
            />
          )}
          <Notification
            details={this._notificationDetails(resources)}
            bodyClass={"state-notification-visible"}
          />
        </div>
      </header>
    )
  }
}

export default injectIntl(Header)



// WEBPACK FOOTER //
// ./webapp/components/modules/Header.js